import React, { useState } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import ReactApexChart from 'react-apexcharts';
import {
  IconUsersGroup,
  IconCalendarOff,
  IconClock,
  IconChartBar,
  IconAlertCircle,
  IconShieldLock,
  IconChevronRight,
  IconAlertTriangle,
  IconBriefcase,
  IconBuilding,
  IconUserCircle,
  IconMail,
  IconHash,
  IconCalendarEvent,
  IconChevronDown,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { useHrDashboard, useMeDashboard } from '../../hooks/useDashboard';
import { useMyProfile } from '../../hooks/useEmployees';
import { useExpiringDocuments } from '../../hooks/useDocuments';
import { useNavigate } from 'react-router-dom';
import { PageLoader, TableLoader } from '../../components/common/Loader';

const AVATAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4', '#8b5cf6'];

// accent can be a hex string or a CSS var() reference — color-mix handles both correctly,
// unlike string concatenation (`${accent}18`) which produces invalid CSS for var() values.
function tint(accent, percent = 15) {
  return `color-mix(in srgb, ${accent} ${percent}%, transparent)`;
}

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function Avatar({ name, size = 56 }) {
  const initials = (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('') || '?';
  const colorIndex = (name || '').length % AVATAR_COLORS.length;
  return (
    <div
      className="flex items-center justify-center rounded-full font-semibold text-white flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: AVATAR_COLORS[colorIndex], fontSize: size / 2.4 }}
    >
      {initials}
    </div>
  );
}

function VitalItem({ icon: Icon, label, value, accent, nowrap }) {
  return (
    <div className="flex items-center gap-4 group cursor-default">
      <div
        className="flex items-center justify-center w-12 h-12 rounded-2xl flex-shrink-0 shadow-sm transition-transform duration-300 group-hover:scale-110"
        style={{ backgroundColor: tint(accent, 15) }}
      >
        <Icon size={20} style={{ color: accent }} className="transition-transform duration-300 group-hover:-rotate-12" />
      </div>
      <div className={nowrap ? 'flex-1' : 'min-w-0 flex-1'}>
        <p className="text-text-secondary text-[11px] uppercase tracking-wider mb-1">{label}</p>
        <p
          className={`text-sm font-semibold ${nowrap ? 'whitespace-nowrap' : 'truncate'}`}
          title={typeof value === 'string' ? value : undefined}
        >
          {value ?? '—'}
        </p>
      </div>
    </div>
  );
}

function CardHeader({ icon: Icon, color, title, suffix, action, onClick }) {
  return (
    <div
      className={`flex items-center justify-between gap-15 mb-20 ${onClick ? 'cursor-pointer group' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-10 min-w-0">
        <div
          className="flex items-center justify-center w-34 h-34 rounded-lg flex-shrink-0"
          style={{ backgroundColor: tint(color) }}
        >
          <Icon size={17} style={{ color }} />
        </div>
        <div className="text-[16px]/[19.2px] font-semibold truncate">
          {title}
          {suffix && <span className="ml-10 text-sm font-normal text-text-secondary">{suffix}</span>}
        </div>
      </div>
      {action ?? (onClick && <IconChevronRight size={16} className="text-text-secondary group-hover:text-primary transition-colors flex-shrink-0" />)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HR DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, accent, to }) {
  const navigate = useNavigate();
  return (
    <div
      className={`bg-bg-dark rounded-xl shadow-light p-20 flex items-center gap-15 border border-border transition-transform ${to ? 'cursor-pointer hover:scale-[1.02] hover:border-primary/40' : ''}`}
      onClick={to ? () => navigate(to) : undefined}
    >
      <div
        className="flex items-center justify-center w-50 h-50 rounded-xl flex-shrink-0"
        style={{ backgroundColor: tint(accent) }}
      >
        <Icon size={22} style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <p className="text-text-secondary text-xs uppercase tracking-wider mb-4">{label}</p>
        <p className="text-[24px]/[28px] font-bold">{value ?? '—'}</p>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, color, dot, to }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-between py-10 border-b border-border last:border-none last:pb-0">
      <div className="flex items-center gap-8">
        <span className="w-8 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
        <span className="text-sm text-text-secondary">{label}</span>
      </div>
      <div className="flex items-center gap-6">
        <span className={`text-[17px]/[20px] font-semibold ${color}`}>{value}</span>
        {to && (
          <button
            type="button"
            onClick={() => navigate(to)}
            className="flex items-center justify-center text-text-secondary hover:text-primary transition-colors"
          >
            <IconChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function HrDashboard({ onSwitch }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgId = user?.orgId;
  const { data, isLoading, isError } = useHrDashboard(orgId);

  // All hooks must be before any early returns

  if (isLoading) {
    return (
      <PageLoader minHeight="256px" />
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-10 p-20 bg-bg-dark rounded-lg shadow-light text-danger">
        <IconAlertCircle size={20} />
        <span>Failed to load HR dashboard data. Please try again.</span>
      </div>
    );
  }

  // ── Field mapping for new API structure ──
  const period = data?.period ?? {};
  const workforce = data?.workforce ?? {};
  const leave = data?.leave ?? {};
  const timesheet = data?.timesheet ?? {};
  const kpi = data?.kpi ?? {};

  const totalEmployees = workforce.total_active_employees ?? 0;
  const genderBreakdown = workforce.gender_breakdown ?? {};
  const genderMale = genderBreakdown.male ?? 0;
  const genderFemale = genderBreakdown.female ?? 0;
  const genderUnknown = genderBreakdown.unknown ?? 0;
  const genderTotal = genderMale + genderFemale + genderUnknown || 1;

  const leaveApproved = leave.approved?.total ?? 0;
  const leavePending = leave.pending?.total ?? 0;
  const leaveRejected = leave.rejected?.total ?? 0;

  const tsApproved = timesheet.approved?.total ?? 0;
  const tsOpen = timesheet.open?.total ?? 0;
  const tsOverdue = timesheet.overdue_submission?.total ?? 0;
  const tsOverdueList = timesheet.overdue_submission?.employees ?? [];
  const tsOpenList = timesheet.open?.employees ?? [];

  const kpiSubmitted = kpi.submitted?.total ?? 0;

  const periodLabel = period.start_date
    ? new Date(period.start_date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : '';

  // Gender chart
  const genderSeries = [genderFemale, genderMale, genderUnknown].filter(v => v > 0);
  const genderLabels = ['Female', 'Male', 'Not Specified'].filter((_, i) =>
    [genderFemale, genderMale, genderUnknown][i] > 0
  );
  const genderColors = ['var(--chart-color5)', 'var(--chart-color1)', 'var(--chart-color3)'].filter(
    (_, i) => [genderFemale, genderMale, genderUnknown][i] > 0
  );
  const genderChart = {
    series: genderSeries,
    options: {
      labels: genderLabels,
      colors: genderColors,
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { colors: 'var(--bg-dark)' },
      plotOptions: {
        pie: {
          expandOnClick: true,
          donut: {
            labels: {
              show: true,
              name: { fontSize: '14px' },
              value: { fontSize: '18px', fontWeight: 600 },
              total: { showAlways: true, show: true, label: 'Total', fontSize: '12px' },
            },
          },
        },
      },
    },
  };

  return (
    <>
      <div className="flex items-center justify-between gap-15 mb-20">
        <Breadcrumb title="HR Dashboard" path="Dashboard" />
        <button onClick={onSwitch} className="btn btn-outline-primary btn-sm flex items-center gap-8">
          <IconUsersGroup size={16} />
          <span>My Personal Dashboard</span>
        </button>
      </div>

      {/* Period label */}
      {periodLabel && (
        <p className="text-sm text-text-secondary mb-16">Showing data for <span className="font-medium">{periodLabel}</span></p>
      )}

      {/* ── Stat Cards ── */}
      <div className="grid md:grid-cols-4 grid-cols-2 gap-20 mb-30">
        <StatCard icon={IconUsersGroup} label="Active Employees" value={totalEmployees} accent="var(--chart-color1)" to="/employee" />
        <StatCard icon={IconCalendarOff} label="Leave Pending" value={leavePending} accent="var(--warning)" to="/dashboard/detail/leave-pending" />
        <StatCard icon={IconClock} label="Timesheets Open" value={tsOpen} accent="var(--chart-color3)" to="/dashboard/detail/open-timesheets" />
        <StatCard icon={IconChartBar} label="KPIs Submitted" value={kpiSubmitted} accent="var(--chart-color5)" to="/dashboard/detail/kpi-submitted" />
      </div>

      {/* ── Summary Row ── */}
      <div className="grid lg:grid-cols-12 grid-cols-1 gap-20 mb-20">

        {/* Leave Summary */}
        <div
          className="lg:col-span-4 bg-bg-dark rounded-xl shadow-light p-20 border border-border"
        >
          <div className="flex items-center justify-between mb-15 pb-12 border-b border-border">
            <p className="text-[15px] font-semibold">Leave Summary</p>
          </div>
          <SummaryRow label="Approved" value={leaveApproved} color="text-success" dot="var(--chart-color2)" to="/dashboard/detail/leave-approved" />
          <SummaryRow label="Pending" value={leavePending} color="text-warning" dot="var(--warning)" to="/dashboard/detail/leave-pending" />
          <SummaryRow label="Rejected" value={leaveRejected} color="text-danger" dot="var(--danger)" to="/dashboard/detail/leave-rejected" />
        </div>

        {/* Timesheet Summary */}
        <div className="lg:col-span-4 bg-bg-dark rounded-xl shadow-light p-20 border border-border">
          <div className="flex items-center justify-between mb-15 pb-12 border-b border-border">
            <p className="text-[15px] font-semibold">Timesheet Summary</p>
          </div>
          <SummaryRow label="Approved" value={tsApproved} color="text-success" dot="var(--chart-color2)" to="/dashboard/detail/timesheet-approved" />
          <SummaryRow label="Open" value={tsOpen} color="text-warning" dot="var(--warning)" to="/dashboard/detail/open-timesheets" />
          {tsOverdue > 0 && (
            <SummaryRow label="Overdue Submission" value={tsOverdue} color="text-danger" dot="var(--danger)" to="/dashboard/detail/overdue-timesheets" />
          )}

          <div className="flex items-center justify-between mt-20 mb-15 pb-12 border-b border-border">
            <p className="text-[15px] font-semibold">KPI Summary</p>
            <button
              type="button"
              onClick={() => navigate('/dashboard/detail/kpi-submitted')}
              className="flex items-center justify-center text-text-secondary hover:text-primary transition-colors"
            >
              <IconChevronRight size={16} />
            </button>
          </div>
          <SummaryRow label="Submitted" value={kpiSubmitted} color="text-blue" dot="var(--chart-color1)" to="/dashboard/detail/kpi-submitted" />
        </div>

        {/* Workforce Gender */}
        <div className="lg:col-span-4 bg-bg-dark rounded-xl shadow-light p-20 border border-border">
          <p className="text-[15px] font-semibold mb-15 pb-12 border-b border-border">Workforce Gender</p>
          {genderSeries.length > 0 ? (
            <>
              <ReactApexChart
                options={genderChart.options}
                series={genderChart.series}
                type="donut"
                className="max-w-[200px] mx-auto"
              />
              <div className="flex flex-col gap-8 mt-15">
                {genderLabels.map((label, i) => (
                  <div key={label} className="flex items-center justify-between">
                    <div className="flex items-center gap-8">
                      <span className="inline-block w-10 h-10 rounded-full" style={{ backgroundColor: genderColors[i] }} />
                      <span className="text-sm">{label}</span>
                    </div>
                    <span className="text-sm font-semibold">
                      {genderSeries[i]} <span className="text-text-secondary font-normal">({Math.round((genderSeries[i] / genderTotal) * 100)}%)</span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-text-secondary text-sm mt-10">No gender data available.</p>
          )}
        </div>

      </div>

      {/* ── Timesheet alert banners ── */}
      {tsOverdueList.length > 0 && (
        <div className="flex flex-col gap-10">
          <button
            onClick={() => navigate('/dashboard/detail/overdue-timesheets')}
            className="w-full flex items-center justify-between gap-12 px-20 py-14 bg-danger/5 border border-danger/30 rounded-xl hover:bg-danger/10 transition-colors text-left"
          >
            <div className="flex items-center gap-12">
              <IconAlertTriangle size={18} className="text-danger flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-danger">Overdue Timesheet Submissions</p>
                <p className="text-xs text-text-secondary mt-1">
                  {tsOverdueList.length} employee{tsOverdueList.length !== 1 ? 's' : ''} have not submitted by cutoff day {timesheet.overdue_submission?.cutoff_day}
                </p>
              </div>
            </div>
            <span className="text-xs text-danger font-medium flex items-center gap-4 flex-shrink-0">
              View all <IconChevronRight size={14} />
            </span>
          </button>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ME DASHBOARD (self-service)
// ─────────────────────────────────────────────────────────────────────────────

function statusBadgeClass(status) {
  switch ((status ?? '').toLowerCase()) {
    case 'approved':
    case 'accepted': return 'text-success border-success bg-success/10';
    case 'rejected': return 'text-danger border-danger bg-danger/10';
    case 'confirmed':
    case 'submitted': return 'text-blue border-blue bg-blue/10';
    default: return 'text-warning border-warning bg-warning/10';
  }
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return { text: 'Good morning', emoji: '☀️' };
  if (hour >= 12 && hour < 17) return { text: 'Good afternoon', emoji: '🌤️' };
  if (hour >= 17 && hour < 21) return { text: 'Good evening', emoji: '🌙' };
  return { text: 'Good night', emoji: '🌟' };
}

function CircularProgress({ pct, accent, size = 56, stroke = 5 }) {
  const r = (size - stroke * 2) / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference - (pct / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke="var(--bg-light-100)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke={accent}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)' }}
      />
    </svg>
  );
}

function MeDashboard({ onSwitch, isHr }) {
  const { user, getRoleDisplayName } = useAuth();
  const navigate = useNavigate();
  const orgId = user?.orgId;
  const { data, isLoading, isError } = useMeDashboard(orgId);
  const { data: employee } = useMyProfile();
  const { data: expiringDocsData } = useExpiringDocuments(30);
  const expiringDocsCount = expiringDocsData?.count || 0;
  const [kpiCollapsed, setKpiCollapsed] = useState(false);

  if (isLoading) {
    return (
      <PageLoader minHeight="256px" />
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-10 p-20 bg-danger/10 border border-danger/20 rounded-2xl shadow-sm text-danger backdrop-blur-sm">
        <IconAlertCircle size={20} />
        <span className="font-medium">Failed to load your dashboard data. Please try again.</span>
      </div>
    );
  }

  const employeeName = data?.employee_name ?? (`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || user?.username);
  const leaveBalances = data?.leave_balances ?? [];
  const recentLeaves = data?.recent_leaves ?? [];
  const kpiSections = data?.kpi_sections ?? [];
  const currentTimesheet = data?.current_timesheet ?? null;
  const kpiStatusCounts = kpiSections.reduce((acc, s) => {
    const key = s.status?.toLowerCase() || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const greeting = getGreeting();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-15 mb-6">
        <Breadcrumb title="My Dashboard" path="Dashboard" />
        {isHr && (
          <button onClick={onSwitch} className="btn btn-outline-primary btn-sm flex items-center gap-8 rounded-full px-6 py-2 hover:shadow-md transition-all">
            <IconShieldLock size={16} />
            <span className="font-semibold">Switch to HR Dashboard</span>
          </button>
        )}
      </div>

      {/* Expiring documents banner */}
      {expiringDocsCount > 0 && (
        <button
          onClick={() => navigate('/documents')}
          className="w-full flex items-center justify-between gap-12 px-20 py-16 bg-gradient-to-r from-warning/10 to-warning/5 border border-warning/30 rounded-2xl hover:scale-[1.01] hover:shadow-md transition-all duration-300 text-left"
        >
          <div className="flex items-center gap-14">
            <div className="p-3 bg-warning/20 rounded-full">
              <IconAlertTriangle size={20} className="text-warning flex-shrink-0" />
            </div>
            <p className="text-[15px] font-semibold text-warning tracking-wide">
              {expiringDocsCount} document{expiringDocsCount !== 1 ? 's' : ''} expiring in the next 30 days
            </p>
          </div>
          <span className="text-sm text-warning font-semibold flex items-center gap-4 flex-shrink-0 bg-warning/10 px-4 py-2 rounded-full">
            View all <IconChevronRight size={16} />
          </span>
        </button>
      )}

      {/* Profile + Vitals - Modernized */}
      <div className="grid md:grid-cols-12 grid-cols-1 gap-6">
        {/* Welcome Hero Card */}
        <div className="md:col-span-8 overflow-hidden rounded-3xl shadow-lg border border-border p-6 md:p-8 bg-bg-dark">
          <div className="flex items-center gap-6 flex-wrap mb-6">
            <div className="relative border-4 border-bg-light rounded-full">
              <Avatar name={employee?.full_name ?? employeeName} size={64} />
            </div>
            <div className="min-w-0">
              <p className="text-primary font-medium text-xs uppercase tracking-widest mb-1">{greeting.emoji} {greeting.text}</p>
              <h2 className="text-[22px]/[26px] font-bold pb-1">
                {employeeName}
              </h2>
              <p className="text-text-secondary mt-1 text-sm flex items-center gap-2">
                <IconBriefcase size={14} className="text-primary/70" />
                {employee?.job_title || getRoleDisplayName()}
                {employee?.department_name ? <span className="opacity-50">|</span> : ''}
                {employee?.department_name}
              </p>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-6 border-t border-border/30">
            <VitalItem icon={IconUserCircle} label="Line Manager" value={employee?.line_manager_name} accent="var(--chart-color2)" />
            <VitalItem icon={IconHash} label="Employee No." value={employee?.employee_no} accent="var(--chart-color4)" />
            <VitalItem
              icon={IconCalendarEvent}
              label="Hire Date"
              value={employee?.hire_date ? new Date(employee.hire_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null}
              accent="var(--success)"
            />
            <VitalItem icon={IconMail} label="Email" value={employee?.email || user?.email} accent="var(--chart-color5)" nowrap />
          </div>
        </div>

        {/* Current Timesheet Mini-card */}
        <div className="md:col-span-4 bg-bg-dark rounded-3xl shadow-lg border border-border p-6 flex flex-col justify-center items-center text-center gap-6 group hover:border-warning/30 transition-colors duration-300">
          <div
            className="flex items-center justify-center w-16 h-16 rounded-2xl flex-shrink-0"
            style={{ backgroundColor: tint('var(--warning)', 15) }}
          >
            <IconClock size={28} style={{ color: 'var(--warning)' }} />
          </div>
          <div className="flex-1 w-full">
            <p className="text-text-secondary text-[11px] uppercase tracking-wider mb-3">Current Timesheet</p>
            {currentTimesheet ? (
              <div className="inline-block">
                <span className={`px-4 py-2 uppercase text-xs font-bold border-2 rounded-full shadow-sm ${statusBadgeClass(currentTimesheet.status)}`}>
                  {currentTimesheet.status}
                </span>
              </div>
            ) : (
              <span className="text-sm font-medium text-text-secondary bg-bg-light px-4 py-2 rounded-full">No active timesheet</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate('/timesheet')}
            className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl bg-bg-light/50 text-text-secondary hover:bg-warning/10 hover:text-warning transition-colors font-medium text-sm mt-2"
          >
            Manage Timesheet <IconChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 grid-cols-1 gap-6">
        {/* Leave Balances */}
        <div className="lg:col-span-4 bg-bg-dark rounded-3xl shadow-lg border border-border p-4 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[15px] font-semibold">Leave Balance</div>
            <button
              type="button"
              onClick={() => navigate('/my-leave-balance')}
              className="w-10 h-10 rounded-full bg-bg-light flex items-center justify-center text-text-secondary hover:bg-primary hover:text-white transition-colors"
            >
              <IconChevronRight size={18} />
            </button>
          </div>
          
          {leaveBalances.length > 0 ? (
            <div>
              {(() => {
                const totalAvailable = round2(leaveBalances.reduce((sum, lb) => sum + (lb.available ?? lb.remaining_days ?? lb.balance ?? 0), 0));
                const totalAllocated = round2(leaveBalances.reduce((sum, lb) => sum + (lb.allocated ?? lb.total_days ?? lb.entitled_days ?? lb.allocated_days ?? 0), 0));
                return (
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border/50">
                    <div
                      className="flex items-center justify-center w-12 h-12 rounded-2xl flex-shrink-0 neumorphic-inset"
                    >
                      <IconCalendarOff size={22} style={{ color: 'var(--chart-color1)' }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-text-secondary text-xs uppercase tracking-wider mb-1">Total Available</p>
                      <p className="text-[17px]/[22px] font-semibold">
                        {totalAvailable}
                        <span className="text-sm font-normal text-text-secondary ml-1">/ {totalAllocated} days</span>
                      </p>
                    </div>
                  </div>
                );
              })()}
              {/* Circular progress rings grid */}
              <div className="grid grid-cols-2 gap-3">
                {leaveBalances.map((lb, i) => {
                  const used = lb.used ?? lb.used_days ?? lb.days_taken ?? 0;
                  const total = lb.allocated ?? lb.total_days ?? lb.entitled_days ?? lb.allocated_days ?? 0;
                  const remaining = round2(lb.available ?? lb.remaining_days ?? lb.balance ?? (total - used));
                  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
                  const accent = ['var(--chart-color1)', 'var(--chart-color2)', 'var(--chart-color3)', 'var(--chart-color4)', 'var(--chart-color5)'][i % 5];
                  const label = lb.leave_type_name ?? lb.leave_type ?? lb.name ?? `Type ${i + 1}`;
                  return (
                    <div key={lb.id ?? i} className="flex flex-col items-center gap-1 p-2 rounded-2xl bg-bg-light">
                      <div className="relative flex items-center justify-center">
                        <CircularProgress pct={pct} accent={accent} size={50} stroke={4} />
                        <span className="absolute text-[12px] font-black" style={{ color: accent }}>{remaining}</span>
                      </div>
                      <p className="text-[11px] font-bold text-text-secondary text-center leading-tight line-clamp-2">{label}</p>
                      <p className="text-[10px] text-text-secondary/70">{pct}% used</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="py-10 text-center flex flex-col items-center">
              <IconCalendarOff size={48} className="text-text-secondary/30 mb-4" />
              <p className="text-[14px] font-medium text-text-secondary">No leave balances set up yet.</p>
            </div>
          )}
        </div>

        {/* Recent Leaves */}
        <div className="lg:col-span-8 bg-bg-dark rounded-3xl shadow-lg border border-border p-4 hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[15px] font-semibold">Recent Leave Requests</div>
            <button
              type="button"
              onClick={() => navigate('/leave')}
              className="text-[13px] font-semibold text-primary flex items-center gap-2 hover:bg-primary/10 px-4 py-2 rounded-full transition-colors"
            >
              View all <IconChevronRight size={16} />
            </button>
          </div>
          {recentLeaves.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px] border-collapse">
                <thead>
                  <tr className="border-b-2 border-border/50">
                    <th align="left" className="pb-3 pt-1 px-4 text-[11px] font-bold uppercase tracking-wider text-text-secondary">From</th>
                    <th align="left" className="pb-3 pt-1 px-4 text-[11px] font-bold uppercase tracking-wider text-text-secondary">To</th>
                    <th align="center" className="pb-3 pt-1 px-4 text-[11px] font-bold uppercase tracking-wider text-text-secondary">Days</th>
                    <th align="right" className="pb-3 pt-1 px-4 text-[11px] font-bold uppercase tracking-wider text-text-secondary">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/30">
                  {recentLeaves.map((lr) => (
                    <tr key={lr.id} className="hover:bg-bg-light/40 transition-colors group">
                      <td className="py-2 px-3 text-sm font-medium group-hover:text-primary transition-colors">{lr.start_date}</td>
                      <td className="py-2 px-3 text-sm font-medium">{lr.end_date}</td>
                      <td className="py-2 px-3 text-sm font-semibold text-center">
                        <span className="bg-bg-light px-3 py-1 rounded-lg">{lr.days_requested}</span>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <span className={`inline-block py-1 px-3 uppercase text-[10px] font-bold border-2 rounded-full shadow-sm ${statusBadgeClass(lr.status)}`}>
                          {lr.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-16 text-center flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-bg-light rounded-full flex items-center justify-center mb-4">
                <IconCalendarEvent size={28} className="text-text-secondary/50" />
              </div>
              <p className="text-[14px] font-medium text-text-secondary">No recent leave requests.</p>
            </div>
          )}
        </div>

        {/* KPI Sections */}
        <div className="lg:col-span-12 bg-bg-dark rounded-3xl shadow-lg border border-border p-6 hover:shadow-xl transition-shadow duration-300">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-6">
            <button
              type="button"
              className="flex items-center gap-4 min-w-0 group"
              onClick={() => setKpiCollapsed((prev) => !prev)}
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-bg-light text-text-secondary group-hover:bg-primary group-hover:text-white shadow-sm transition-all flex-shrink-0">
                {kpiCollapsed ? <IconChevronRight size={16} /> : <IconChevronDown size={16} />}
              </span>
              <span className="text-[15px] font-semibold truncate text-left">
                KPI Sections
                {kpiSections[0]?.cycle_name && (
                  <span className="ml-10 text-sm font-normal text-text-secondary">
                    — {kpiSections[0].cycle_name}
                  </span>
                )}
              </span>
            </button>
            <div className="flex items-center gap-3 flex-wrap sm:justify-end">
              {Object.entries(kpiStatusCounts).map(([status, count]) => (
                <span key={status} className={`px-3 py-1 rounded-full text-[11px] font-bold capitalize whitespace-nowrap border-2 shadow-sm ${statusBadgeClass(status)}`}>
                  {count} {status}
                </span>
              ))}
              <span
                className="text-[13px] font-semibold text-primary flex items-center gap-2 hover:bg-primary/10 px-4 py-2 rounded-full cursor-pointer transition-colors ml-2"
                onClick={() => navigate('/performance/kpi')}
              >
                View Details <IconChevronRight size={16} />
              </span>
            </div>
          </div>
          
          <div className={`grid transition-all duration-300 ease-in-out ${kpiCollapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'}`}>
            <div className="overflow-hidden">
              {kpiSections.length > 0 ? (
                <div className="overflow-x-auto pt-4 border-t-2 border-border/50">
                  <table className="w-full min-w-[500px] border-collapse">
                    <thead>
                      <tr className="border-b-2 border-border/50">
                        <th align="left" className="pb-3 pt-1 px-4 text-[11px] font-bold uppercase tracking-wider text-text-secondary">Section Name</th>
                        <th align="right" className="pb-3 pt-1 px-4 text-[11px] font-bold uppercase tracking-wider text-text-secondary">Current Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                      {kpiSections.map((kpi, i) => (
                        <tr key={i} className="hover:bg-bg-light/40 transition-colors group">
                          <td className="py-3 px-4 text-sm group-hover:text-primary transition-colors">{kpi.section_name}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={`inline-block py-1 px-3 uppercase text-[10px] font-bold border-2 rounded-full shadow-sm ${statusBadgeClass(kpi.status)}`}>
                              {kpi.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="pt-6 pb-2 text-center border-t-2 border-border/50">
                  <p className="text-[14px] font-medium text-text-secondary bg-bg-light inline-block px-6 py-2 rounded-xl">No KPI sections assigned for the current cycle.</p>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUPER ADMIN DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
function SuperAdminDashboard() {
  const { user } = useAuth();
  return (
    <>
      <Breadcrumb title="System Dashboard" path="Dashboard" />
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-20">
        <div className="flex items-center justify-center w-70 h-70 rounded-full bg-org-blue-primary/10 border border-org-blue-primary/20">
          <IconShieldLock size={32} className="text-org-blue-primary" />
        </div>
        <div className="text-center">
          <h2 className="text-[22px]/[28px] font-bold mb-8">
            Welcome, {user?.first_name || user?.username}
          </h2>
          <p className="text-text-secondary text-sm max-w-[400px]">
            You are logged in as a System Administrator. Use the sidebar to manage organizations, users, and system settings.
          </p>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT DASHBOARD — routes based on user type
// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user, hasAnyPermission } = useAuth();
  const isHr = hasAnyPermission(['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve']);
  const [viewMode, setViewMode] = useState(isHr ? 'hr' : 'me');

  if (user?.is_superuser) return <SuperAdminDashboard />;

  if (viewMode === 'hr' && isHr) {
    return <HrDashboard onSwitch={() => setViewMode('me')} />;
  }

  return <MeDashboard onSwitch={() => setViewMode('hr')} isHr={isHr} />;
}
